import type { EvaluateResponse, PostcodeResponse, SubmissionRequest, SubmissionResponse } from '../domain/contract';
import type { ChecklistGate } from '../domain/settings';

export type Screen = 'landing' | 'role' | 'you' | 'check' | 'checking' | 'result' | 'secure' | 'booking' | 'done';

export const STEP_SCREENS: Screen[] = ['role', 'you', 'check'];

export type Flow = {
  screen: Screen;
  companyQuery: string;
  company: { number: string | null; name: string } | null;
  companyUnlisted: boolean;
  jobTitle: string;
  aiNote: string;
  postcode: string;
  postcodeLookup: (PostcodeResponse & { for: string }) | null;
  salary: string;
  employeeBand: string | null;
  priority: string | null;
  checks: Record<ChecklistGate, boolean>;
  tickMode: boolean;
  hires: number;
  evaluation: EvaluateResponse | null;
  firstName: string;
  lastName: string;
  email: string;
  consent: boolean;
  marketing: boolean;
  waitOption: 'waitlist' | 'talk';
  pendingRequest: SubmissionRequest | null;
  submission: SubmissionResponse | null;
  meetingAt: string | null;
  bookingDeferred: boolean;
};

export const ALL_CHECKS = (value: boolean): Record<ChecklistGate, boolean> => ({ G1: value, G4: value, G5: value, G6: value, G7: value, G8: value, G9: value });

export function initialFlow(): Flow {
  return {
    screen: 'landing',
    companyQuery: '',
    company: null,
    companyUnlisted: false,
    jobTitle: '',
    aiNote: '',
    postcode: '',
    postcodeLookup: null,
    salary: '',
    employeeBand: null,
    priority: null,
    checks: ALL_CHECKS(false),
    tickMode: false,
    hires: 1,
    evaluation: null,
    firstName: '',
    lastName: '',
    email: '',
    consent: false,
    marketing: false,
    waitOption: 'waitlist',
    pendingRequest: null,
    submission: null,
    meetingAt: null,
    bookingDeferred: false,
  };
}

export const FLOW_KEY = 'fh.flow.v1';

export function parseSalary(value: string): number {
  const n = Number.parseFloat(value.replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? Math.round(n) : 0;
}

export function companyName(flow: Flow): string {
  return flow.company?.name ?? flow.companyQuery.trim();
}

export function answersOf(flow: Flow): SubmissionRequest['answers'] {
  return {
    jobTitle: flow.jobTitle.trim(),
    aiNote: flow.aiNote.trim(),
    postcode: flow.postcode.trim(),
    salary: parseSalary(flow.salary),
    employeeBand: flow.employeeBand ?? '',
    priority: flow.priority ?? '',
    checks: flow.checks,
    companyNumber: flow.companyUnlisted ? null : (flow.company?.number ?? null),
  };
}

/** Totals for the hires counter, from the server's per-hire values. */
export function totalFor(firstHire: number, additionalHire: number, hires: number): number {
  return firstHire + (hires - 1) * additionalHire;
}
